/**
 * Copyright © 2026 Adnuntius AS.
 * UI-Router state declarations.
 */
import {Ng2StateDeclaration} from '@uirouter/angular';
import {firstValueFrom, tap} from 'rxjs';

import {UserProfileSaverService} from '../../components/session/user-profile-saver.service';
import {SignalAdminResourceContextActionsComponent} from '../common/controller/signal-admin-resource-context-actions.component';
import {SignalAdminResourcePageState} from '../common/controller/signal-admin-resource-page-state';
import {SignalAdminResourceTitleComponent} from '../common/controller/signal-admin-resource-title.component';
import {ListDefaultActionsComponent} from '../common/directives/list-default-actions.component';
import {stateTitleView} from '../common/directives/html/state-title.component';
import {AdnRouterHelperApi, StateDeclarationBuilder} from '../routing/state-utils';
import {PoliticalEntityApiService, PoliticalEntityModel} from './political-entity-api.service';
import {PoliticalEntityItemPageComponent} from './political-entity-item-page.component';
import {PoliticalEntityRoutePageComponent} from './political-entity-route-page.component';
import politicalEntityCrumbs from './political-entity-crumbs';

export function politicalEntityStates(adnRouterHelper: AdnRouterHelperApi): Ng2StateDeclaration[] {
  const states = new StateDeclarationBuilder();
  const api = adnRouterHelper.services.service(PoliticalEntityApiService);
  const userProfileSaver = adnRouterHelper.services.service(UserProfileSaverService);
  const permissions = adnRouterHelper.services.localUserPermissions();
  const readOnly = () => !permissions.canEditPoliticalResources();
  const rh = adnRouterHelper.createRouterHelper('political-entities.political-entity', PoliticalEntityApiService, 'app.pol', 'app');

  states.add(rh.listPath(), rh.list({
    data: {app: 'POLITICAL'},
    resolve: {listReadOnly: readOnly},
    views: {
      'content@app': {component: PoliticalEntityRoutePageComponent, bindings: {readOnly: 'listReadOnly'}},
      'context@app': {component: ListDefaultActionsComponent, bindings: {readOnly: 'listReadOnly', newStateName: 'listNewStateName'}},
      'title@app': stateTitleView()
    }
  }));
  states.add(rh.newItemPath(), rh.newItem());
  states.add(rh.itemPath(), rh.item({
    data: {app: 'POLITICAL'},
    resolve: {
      politicalEntityPage: function($stateParams: any) {
        if ($stateParams.isNew) {
          return new SignalAdminResourcePageState<PoliticalEntityModel>(
            {id: $stateParams.id, objectState: 'ACTIVE', validationWarnings: []}, true, readOnly(), api,
            'Political Entity', 'name', undefined, undefined,
            {objectTypeKey: 'PoliticalEntity', crumbParams: politicalEntityCrumbs}
          );
        }
        return firstValueFrom(api.get($stateParams.id).pipe(
          tap((model) => userProfileSaver.saveObj(model, 'adnLastViewed'))
        )).then((model) => new SignalAdminResourcePageState(
          model, false, readOnly(), api, 'Political Entity', 'name', undefined, undefined,
          {objectTypeKey: 'PoliticalEntity', crumbParams: politicalEntityCrumbs}
        ));
      },
      model: function(politicalEntityPage: SignalAdminResourcePageState<PoliticalEntityModel>) {
        return politicalEntityPage.model();
      }
    },
    views: {
      'content@app': {component: PoliticalEntityItemPageComponent, bindings: {state: 'politicalEntityPage'}},
      'context@app': {component: SignalAdminResourceContextActionsComponent, bindings: {state: 'politicalEntityPage'}},
      'title@app': {component: SignalAdminResourceTitleComponent, bindings: {state: 'politicalEntityPage'}}
    }
  }));
  return states.build();
}
